import { useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import { updateServiceEvent } from "@/lib/service/client";
import { openOpsPaperworkDeal } from "@/lib/opsLookup/openOpsPaperworkDeal";
import { serviceJobKindLabel } from "@/lib/service/serviceJobKind";
import type { useServiceBoardPanel } from "./useServiceBoardPanel";
import {
  EVENT_STATUS_LABELS,
  ghostBtnClass,
  inputClass,
  primaryBtnClass,
  queueLabel,
  rigLabel,
  techLabel,
} from "./serviceShared";
import { ServiceEventCheckInPanel } from "./ServiceEventCheckInPanel";
import { ServiceEventCostPanel } from "./ServiceEventCostPanel";
import { ServiceEventLineItemsPanel } from "./ServiceEventLineItemsPanel";

type Props = {
  d: ReturnType<typeof useServiceBoardPanel>;
  scheduleDate: string;
  setScheduleDate: (v: string) => void;
  scheduleWindow: string;
  setScheduleWindow: (v: string) => void;
  onSchedule: () => void;
};

export function ServiceBoardEventDetail({
  d,
  scheduleDate,
  setScheduleDate,
  scheduleWindow,
  setScheduleWindow,
  onSchedule,
}: Props) {
  const event = d.selectedEvent;
  const [contactBusy, setContactBusy] = useState(false);
  if (!event) return null;

  const contacted = Boolean(event.customer_contacted_at);

  return (
    <SectionCard title="Event detail">
      <div className="space-y-2 text-sm text-gray-800 dark:text-zinc-200">
        <div>
          <strong>Status:</strong> {EVENT_STATUS_LABELS[event.status]}
        </div>
        <div>
          <strong>Kind:</strong> {serviceJobKindLabel(event.job_kind)}
          {event.job_kind === "internal"
            ? " · original deal, no new deal"
            : " · own deal"}
        </div>
        <div>
          <strong>Job type:</strong> {queueLabel(d.queues, event.queue_id)}
        </div>
        {event.ops_deal_id ? (
          <div className="flex flex-wrap items-center gap-2">
            <span>
              <strong>Deal:</strong> {event.ops_deal_id.slice(0, 8)}…
            </span>
            <button
              type="button"
              className={ghostBtnClass}
              onClick={() => openOpsPaperworkDeal(event.ops_deal_id!)}
            >
              Open in Ops
            </button>
          </div>
        ) : (
          <div className="text-xs text-amber-800 dark:text-amber-200">
            {event.job_kind === "internal"
              ? "No original deal linked. Pick it from Deals when creating the work order."
              : "No service deal yet — sold jobs mint one when the work order is approved onto the board."}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <span>
            <strong>Customer:</strong> {contacted ? "Contacted" : "Not contacted"}
          </span>
          <button
            type="button"
            className={ghostBtnClass}
            disabled={contactBusy}
            onClick={() => {
              setContactBusy(true);
              void updateServiceEvent({
                event_id: event.id,
                customer_contacted: !contacted,
              }).then(async (res) => {
                setContactBusy(false);
                if (res.ok) await d.reload();
              });
            }}
          >
            {contactBusy ? "Saving…" : contacted ? "Clear contacted" : "Mark contacted"}
          </button>
        </div>
        <ServiceEventCheckInPanel
          eventId={event.id}
          checkInStatus={event.check_in_status}
          onRecorded={() => void d.reload()}
        />
        <div>
          <strong>Tech:</strong> {techLabel(d.techs, event.tech_id)}
        </div>
        <div>
          <strong>Rig:</strong> {rigLabel(d.rigs, event.assigned_rig_id)}
        </div>
        {event.required_rig_id ? (
          <div>
            <strong>Required rig:</strong> {rigLabel(d.rigs, event.required_rig_id)}
          </div>
        ) : null}
        {event.description ? (
          <p className="text-gray-600 dark:text-zinc-400">{event.description}</p>
        ) : null}
      </div>

      <ServiceEventCostPanel event={event} />
      <ServiceEventLineItemsPanel event={event} />

      <div className="mt-4 grid gap-2 sm:grid-cols-3">
        <input
          type="date"
          className={inputClass}
          value={scheduleDate}
          onChange={(e) => setScheduleDate(e.target.value)}
        />
        <input
          className={inputClass}
          value={scheduleWindow}
          onChange={(e) => setScheduleWindow(e.target.value)}
          placeholder="Window label"
        />
        <button
          type="button"
          className={primaryBtnClass}
          disabled={d.actionBusy || !scheduleDate}
          onClick={onSchedule}
        >
          {d.actionBusy ? "Saving…" : "Schedule"}
        </button>
      </div>
    </SectionCard>
  );
}
